import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type {
  NativeGitActionResult,
  NativeGitGenerationContext,
} from '@crowdex/workspace-protocol';
import type {
  PullRequestCockpit,
  PullRequestMutation,
} from '@crowdex/workspace-protocol/pull-request-cockpit';
import {
  commitNativeGitChanges,
  createNativeGitBranch,
  createNativeGitPullRequest,
  mutateNativeGitPullRequest,
  pushNativeGitBranch,
  readNativeGitGenerationContext,
  readNativePullRequestCockpit,
} from '@/lib/native-desktop';

/**
 * The session a pull request belongs to: a local checkout is read and changed through the local
 * CLI, a cloud task through its core routes. The forge behind it (GitHub, GitLab…) is the core's
 * `forge.v1` provider, never named here.
 */
export type PullRequestSession = {
  workspace: string;
  taskId: string;
  cloud: boolean;
};

const COCKPIT_REUSE_MS = 2_000;
const cockpitReads = new Map<string, { at: number; value: Promise<PullRequestCockpit | null> }>();

/**
 * Views of one checkout (the header chip, the summary, the panel) ask for the same pull request
 * together; they share one native read instead of each querying the forge.
 */
function sharedPullRequestCockpit(workspaceRoot: string): Promise<PullRequestCockpit | null> {
  const cached = cockpitReads.get(workspaceRoot);
  if (cached && Date.now() - cached.at < COCKPIT_REUSE_MS) return cached.value;
  const value: Promise<PullRequestCockpit | null> = readNativePullRequestCockpit(workspaceRoot);
  cockpitReads.set(workspaceRoot, { at: Date.now(), value });
  value.catch(() => {
    if (cockpitReads.get(workspaceRoot)?.value === value) cockpitReads.delete(workspaceRoot);
  });
  return value;
}

/** Drops the shared read of a checkout once its pull request changed. */
function forgetPullRequestCockpit(workspaceRoot: string): void {
  cockpitReads.delete(workspaceRoot);
}

function cloudPullRequestPath(taskId: string): string {
  return `/api/source-control/tasks/${encodeURIComponent(taskId)}/pull-request`;
}

/** The session's pull request with its checks, reviews and threads; null when there is none. */
export async function readPullRequestCockpit(
  session: PullRequestSession,
): Promise<PullRequestCockpit | null> {
  if (!session.cloud) return sharedPullRequestCockpit(session.workspace);
  return apiFetch<{ cockpit: PullRequestCockpit | null }>(
    `${cloudPullRequestPath(session.taskId)}/cockpit`,
    { credentials: 'include', cache: 'no-store' },
  ).then(({ cockpit }) => cockpit);
}

/** Applies a change to the session's pull request (edit, review, merge…). */
export async function mutatePullRequest(
  session: PullRequestSession,
  mutation: PullRequestMutation,
): Promise<void> {
  if (session.cloud) {
    await apiFetch(`${cloudPullRequestPath(session.taskId)}/action`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(mutation),
    });
    return;
  }
  try {
    await mutateNativeGitPullRequest({ workspaceRoot: session.workspace, ...mutation });
  } finally {
    forgetPullRequestCockpit(session.workspace);
  }
}

/** The diffs and history the session's model writes commit and pull request text from. */
export function readGitGenerationContext(
  workspaceRoot: string,
  paths?: readonly string[],
): Promise<NativeGitGenerationContext> {
  return readNativeGitGenerationContext(workspaceRoot, paths);
}

export function createGitBranch(workspaceRoot: string, branch: string): Promise<void> {
  return createNativeGitBranch(workspaceRoot, branch);
}

/** Commits `paths` (a conversation's files), or every change of the checkout, without pushing. */
export function commitGitChanges(
  workspaceRoot: string,
  message: string,
  paths?: readonly string[],
): Promise<NativeGitActionResult> {
  return commitNativeGitChanges({
    workspaceRoot,
    message,
    push: false,
    ...(paths === undefined ? {} : { paths }),
  });
}

export function pushGitBranch(workspaceRoot: string): Promise<NativeGitActionResult> {
  return pushNativeGitBranch(workspaceRoot);
}

/** Opens a pull request for the checkout's pushed branch. */
export function createPullRequest(
  workspaceRoot: string,
  input: { title: string; body: string; draft: boolean },
): Promise<NativeGitActionResult> {
  forgetPullRequestCockpit(workspaceRoot);
  return createNativeGitPullRequest({ workspaceRoot, ...input });
}
